import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  Fade,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { API_CONFIG } from '../config/config';
import logo from '../assets/logo_blau.png';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const validationSchema = yup.object({
  first_name: yup
    .string()
    .required('Vorname ist erforderlich'),
  last_name: yup
    .string()
    .required('Nachname ist erforderlich'),
  email: yup
    .string()
    .email('Gib eine gültige E-Mail-Adresse ein')
    .required('E-Mail-Adresse ist erforderlich'),
  password: yup
    .string()
    .min(12, 'Das Passwort sollte mindestens 12 Zeichen lang sein')
    .max(128, 'Das Passwort sollte nicht länger als 128 Zeichen sein')
    .matches(/^[\s\S]*$/, 'Das Passwort kann beliebige Zeichen enthalten')
    .required('Neues Passwort ist erforderlich'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwörter müssen übereinstimmen')
    .required('Passwort-Bestätigung ist erforderlich'),
});

const Register = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setError('');
      setSuccess(null);
      setLoading(true);

      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email.toLowerCase().trim(),
            password: values.password,
            confirm_password: values.confirmPassword,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 429) {
            // Handle rate limit error
            const retryAfter = response.headers.get('Retry-After');
            const waitTime = retryAfter ? parseInt(retryAfter) : 60;
            throw new Error(`Zu viele Anfragen. Bitte warte ${waitTime} Sekunden, bevor du es erneut versuchst.`);
          }
          if (response.status >= 500) {
            throw new Error(data.detail || 'Registrierung fehlgeschlagen');
          }
          throw new Error(data.detail || 'Registrierung fehlgeschlagen');
        }

        setSuccess(data);
        formik.resetForm();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
  });

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          <Fade in={true} timeout={500}>
            <Box>
              <CheckCircleOutlineIcon 
                sx={{ 
                  fontSize: 60, 
                  color: 'success.main',
                  mb: 2
                }} 
              />
              <Typography variant="h5" gutterBottom>
                {success.message}
              </Typography>
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  '& .MuiAlert-message': {
                    fontSize: '1rem',
                    fontWeight: 500
                  }
                }}
              >
                {success.detail}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Bitte überprüfe deine E-Mail-Adresse: {success.email}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/login')}
                sx={{ mt: 2 }}
              >
                Zurück zur Anmeldeseite
              </Button>
            </Box>
          </Fade>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{ width: '100%', maxWidth: 300, mb: 3 }}
          />
          <Typography component="h1" variant="h5" gutterBottom>
            Konto erstellen
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
            <TextField
              fullWidth
              id="first_name"
              name="first_name"
              label="Vorname"
              value={formik.values.first_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.first_name && Boolean(formik.errors.first_name)}
              helperText={formik.touched.first_name && formik.errors.first_name}
              margin="normal"
            />
            <TextField
              fullWidth
              id="last_name"
              name="last_name"
              label="Nachname"
              value={formik.values.last_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.last_name && Boolean(formik.errors.last_name)}
              helperText={formik.touched.last_name && formik.errors.last_name}
              margin="normal"
            />
            <TextField
              fullWidth
              id="email"
              name="email"
              label="E-Mail-Adresse"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              margin="normal"
            />
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Passwort"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PasswordStrengthMeter password={formik.values.password} />
            
            {formik.values.password && (
              <TextField
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                label="Passwort bestätigen"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                backgroundColor: 'black',
                '&:hover': {
                  backgroundColor: '#333333',
                }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Registrieren'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Du hast bereits ein Konto? Melde dich hier an
              </Link>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register; 